import React, { useEffect, useState } from "react";
import API from "../services/api";
import "./schools.css";

export default function Schools() {

    const [schools, setSchools] = useState([]);
    const [openRow, setOpenRow] = useState(null);
    const [showPasswordId, setShowPasswordId] = useState(null);

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Password copied");
        } catch {
            alert("Copy failed");
        }
    };

    // =========================
    // EXPIRY STATUS
    // =========================
    const getExpiryStatus = (dateStr) => {

        if (!dateStr) return "expired";

        const today = new Date();
        const expiry = new Date(dateStr);

        if (expiry < today) return "expired";

        const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

        if (diffDays <= 15) return "soon";

        return "active";
    };

    // =========================
    // LOAD SCHOOLS
    // =========================
    const loadSchools = async () => {

        const res = await API.get("/schools/");

        setSchools(res.data.schools || []);

    };

    useEffect(() => {

        loadSchools();

    }, []);

    const activeSchools = schools.filter((s) => s.is_active).length;
    const inactiveSchools = schools.length - activeSchools;
    const expiringSchools = schools.filter((s) => getExpiryStatus(s.plan_expiry) === "soon").length;

    // =========================
    // TOGGLE ROW
    // =========================
    const toggleRow = (id, e) => {

        e.stopPropagation();

        setOpenRow(openRow === id ? null : id);

    };

    // =========================
    // ACTIVATE / DEACTIVATE
    // =========================
    const toggleStatus = async (id, e) => {

        e.stopPropagation();

        try {

            await API.post(`/schools/${id}/toggle-status/`);

            loadSchools();

        } catch (err) {

            console.error(err);

            alert("Status update failed");

        }

    };

    // =========================
    // DELETE SCHOOL
    // =========================
    const deleteSchool = async (id, e) => {

        e.stopPropagation();

        if (!window.confirm("Delete this school?")) return;

        await API.delete(`/schools/delete/${id}/`);

        loadSchools();

    };

    // =========================
    // UPDATE RAZORPAY
    // =========================
    const updateRazorpay = async (id, key, secret) => {

    try {

        await API.put(`/schools/${id}/update/`, {
            razorpay_key_id: key,
            razorpay_key_secret: secret
        });

        alert("Razorpay updated");

        loadSchools();

    } catch (err) {

        console.error(err);

        alert("Update failed");

    }

};

    // =========================
    // UI
    // =========================
    return (

        <div className="schools-container">

            <div className="schools-header">
                <div>
                    <span className="page-kicker">School Accounts</span>
                    <h2>All Schools</h2>
                    <p>Manage tenant schools, admin access, plan expiry, and payment gateway keys.</p>
                </div>
                <a href="/schools/add" className="btn-primary">+ Add School</a>
            </div>

            <div className="schools-summary-grid">
                <div className="schools-summary-card">
                    <span>Total Schools</span>
                    <strong>{schools.length}</strong>
                </div>
                <div className="schools-summary-card active">
                    <span>Active</span>
                    <strong>{activeSchools}</strong>
                </div>
                <div className="schools-summary-card inactive">
                    <span>Inactive</span>
                    <strong>{inactiveSchools}</strong>
                </div>
                <div className="schools-summary-card warning">
                    <span>Expiring Soon</span>
                    <strong>{expiringSchools}</strong>
                </div>
            </div>

            <div className="schools-table-wrapper">

                <table className="schools-table">

                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>School Name</th>
                            <th>Plan</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>

                    <tbody>

                        {schools.length === 0 && (
                            <tr>
                                <td colSpan="5" className="no-data">
                                    No schools found
                                </td>
                            </tr>
                        )}

                        {schools.map((s) => {

                            let keyId = s.razorpay_key_id || "";
                            let secret = s.razorpay_key_secret || "";

                            return (

                                <React.Fragment key={s.id}>

                                    {/* MAIN ROW */}

                                    <tr
                                        className="school-row"
                                        style={{ cursor: "pointer" }}
                                    >

                                        <td>{s.id}</td>

                                        <td
                                            className="school-name clickable"
                                            onClick={(e) => toggleRow(s.id, e)}
                                        >
                                            {s.name}
                                        </td>

                                        <td>
                                            <span className={`plan-badge ${s.plan?.name}`}>
                                                {s.plan?.name?.toUpperCase()}
                                            </span>
                                        </td>

                                        <td>

                                            {s.is_active ?

                                                <span className="status-active">Active</span>

                                                :

                                                <span className="status-inactive">Inactive</span>

                                            }

                                        </td>

                                        <td>

                                            <button
                                                className="btn-edit"
                                                onClick={(e) => toggleStatus(s.id, e)}
                                            >
                                                {s.is_active ? "Deactivate" : "Activate"}
                                            </button>

                                            <button
                                                className="btn-delete"
                                                onClick={(e) => deleteSchool(s.id, e)}
                                            >
                                                Delete
                                            </button>

                                        </td>

                                    </tr>

                                    {/* DETAILS ROW */}

                                    {openRow === s.id && (

                                        <tr className="details-row">

                                            <td colSpan="5">

                                                <div className="details-box">

                                                    <h4 className="section-title">School Details</h4>

                                                    <div className="details-grid">

                                                        <p><span>Email:</span> {s.contact_email}</p>

                                                        <p><span>Phone:</span> {s.contact_phone}</p>

                                                        <p><span>Subdomain:</span> {s.subdomain}.navith.cloud</p>

                                                        <p><span>Custom Domain:</span> {s.custom_domain || "-"}</p>

                                                    </div>

                                                    <h4 className="section-title">Login Info</h4>

                                                    <div className="details-grid">

                                                        <p><span>Admin User:</span> {s.admin_username}</p>

                                                        <p><span>Admin Email:</span> {s.admin_email}</p>

                                                        <div className="password-row">

                                                            <span><strong>Password:</strong></span>

                                                            <span className="password-text">
                                                                {showPasswordId === s.id ? s.admin_password : "••••••"}
                                                            </span>

                                                            <button
                                                                className="btn-eye"
                                                                onClick={() =>
                                                                    setShowPasswordId(showPasswordId === s.id ? null : s.id)
                                                                }
                                                            >
                                                                {showPasswordId === s.id ? "Hide" : "Show"}
                                                            </button>

                                                            <button
                                                                className="btn-copy"
                                                                onClick={() => copyToClipboard(s.admin_password)}
                                                            >
                                                                Copy
                                                            </button>

                                                        </div>

                                                    </div>

                                                    <h4 className="section-title">Plan Settings</h4>

                                                    <div className="details-grid">

                                                        <p><span>Plan:</span> {s.plan?.name}</p>

                                                        <p>
                                                            <span>Expiry:</span>

                                                            {s.plan_expiry ?

                                                                <span className={`expiry-tag ${getExpiryStatus(s.plan_expiry)}`}>
                                                                    {s.plan_expiry}
                                                                </span>

                                                                :

                                                                "N/A"}

                                                        </p>

                                                    </div>

                                                    <h4 className="section-title">Razorpay Payment Settings</h4>

                                                    <div className="details-grid">

                                                        <input
                                                            placeholder="Razorpay Key"
                                                            defaultValue={keyId}
                                                            className="input-field"
                                                            onChange={(e) => keyId = e.target.value}
                                                        />

                                                        <input
                                                            placeholder="Razorpay Secret"
                                                            type="password"
                                                            defaultValue={secret}
                                                            className="input-field"
                                                            onChange={(e) => secret = e.target.value}
                                                        />

                                                    </div>

                                                    <button
                                                        className="btn-primary"
                                                        style={{ marginTop: 15 }}
                                                        onClick={() => updateRazorpay(s.id, keyId, secret)}
                                                    >
                                                        Save Razorpay Settings
                                                    </button>

                                                    <h4 className="section-title">Meta</h4>

                                                    <p className="meta-text">
                                                        Created: {new Date(s.created_at).toLocaleString()}
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    )}

                                </React.Fragment>

                            );

                        })}

                    </tbody>

                </table>

            </div>

        </div>

    );

}
